import { createTheme } from "@mui/material/styles";
import "../fonts/Famous.ttf";
import "../fonts/OptimaLTStd-Bold.otf";
import "../fonts/OptimaLTStd-BoldItalic.otf";
import "../fonts/OptimaLTStd-DemiBold.otf";
import "../fonts/OptimaLTStd-DemiBoldItalic.otf";
import "../fonts/OptimaLTStd-ExtraBlack.otf";
import "../fonts/OptimaLTStd-Italic.otf";
import "../fonts/OptimaLTStd-Medium.otf";
import "../fonts/OptimaLTStd-MediumItalic.otf";

import {
  primary,
  secondary,
  tertiary,
  error,
  success,
  neutral,
  neutral_variant
} from "./colors";

import { defaultTheme } from "react-admin";

function pxToRemConversion(value: number) {
  return `${value / 14}rem`;
}

const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  demibold: 600
};

const breakpointValues = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

const myTheme = createTheme();

const pointmentTheme = createTheme({
  palette: {
    ...myTheme.palette,
    primary: {
      ...primary,
      light: primary[90],
      main: primary[40],
      dark: primary[10],
      contrastText: primary[99]
    },
    secondary: {
      ...secondary,
      light: secondary[90],
      main: secondary[40],
      dark: secondary[10],
      contrastText: secondary[99]
    },
    tertiary: {
      ...tertiary,
      light: tertiary[90],
      main: tertiary[40],
      dark: tertiary[10],
      contrastText: tertiary[99]
    },
    error: {
      ...error,
      light: error[90],
      main: error[40],
      dark: error[10],
      contrastText: error[99]
    },
    success: {
      ...success,
      light: success[90],
      main: success[40],
      dark: success[10],
      contrastText: success[99]
    },
    neutral: {
      ...neutral,
      light: neutral[90],
      main: neutral[40],
      dark: neutral[10],
      contrastText: neutral[99]
    },
    neutral_variant: {
      ...neutral_variant,
      light: neutral_variant[90],
      main: neutral_variant[40],
      dark: neutral_variant[10],
      contrastText: neutral_variant[99]
    },
    surface: {
      ...neutral_variant,
      light: neutral_variant[90],
      main: neutral_variant[40],
      dark: neutral_variant[10],
      contrastText: neutral_variant[99]
    },
    background: {
      default: neutral[90],
      paper: neutral[70],
      light: neutral[90],
      main: neutral[40],
      dark: neutral[10]
    }
  },
  breakpoints: {
    ...myTheme.breakpoints,
    unit: "px",
    values: breakpointValues
  }
});

pointmentTheme.typography = {
  body_large: {
    fontFamily: "Inter",
    fontWeight: fontWeights.regular,
    fontSize: pxToRemConversion(16)
  },
  body_large_bold: {
    fontFamily: "Inter",
    fontWeight: fontWeights.demibold,
    fontSize: pxToRemConversion(16)
  },
  body_small: {
    fontFamily: "Inter",
    fontWeight: fontWeights.regular,
    fontSize: pxToRemConversion(14)
  },
  body_small_bold: {
    fontFamily: "Inter",
    fontWeight: fontWeights.demibold,
    fontSize: pxToRemConversion(14)
  },
  button_large: {
    fontFamily: "Inter",
    fontWeight: fontWeights.medium,
    fontSize: pxToRemConversion(15)
  },
  button_small: {
    fontFamily: "Inter",
    fontWeight: fontWeights.medium,
    fontSize: pxToRemConversion(13)
  },
  caption_large: {
    fontFamily: "Inter",
    fontWeight: fontWeights.regular,
    fontSize: pxToRemConversion(12)
  },
  caption_large_bold: {
    fontFamily: "Inter",
    fontWeight: fontWeights.demibold,
    fontSize: pxToRemConversion(12)
  },
  caption_small: {
    fontFamily: "Inter",
    fontWeight: fontWeights.regular,
    fontSize: pxToRemConversion(11)
  },
  caption_small_bold: {
    fontFamily: "Inter",
    fontWeight: fontWeights.demibold,
    fontSize: pxToRemConversion(11)
  },
  h1: {
    fontFamily: "OptimaLTStd",
    fontWeight: fontWeights.medium,
    fontSize: pxToRemConversion(40),
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(48)
    }
  },
  h2: {
    fontFamily: "OptimaLTStd",
    fontWeight: fontWeights.medium,
    fontSize: pxToRemConversion(32),
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(36)
    }
  },
  h3: {
    fontFamily: "OptimaLTStd",
    fontSize: pxToRemConversion(24),
    fontWeight: fontWeights.demibold,
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(30)
    }
  },
  h4: {
    fontFamily: "OptimaLTStd",
    fontSize: pxToRemConversion(21),
    fontWeight: fontWeights.demibold,
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(24)
    }
  },
  h5: {
    fontFamily: "OptimaLTStd",
    fontSize: pxToRemConversion(18),
    fontWeight: fontWeights.demibold,
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(21)
    }
  },
  h6: {
    fontFamily: "OptimaLTStd",
    fontSize: pxToRemConversion(12),
    fontWeight: fontWeights.demibold,
    [pointmentTheme.breakpoints.up("md")]: {
      fontSize: pxToRemConversion(13)
    }
  },
  subtitle1: {},
  subtitle2: {},
  body1: {},
  body2: {},
  caption: {},
  button: {},
  overline: {},
  fontFamily: "Inter",
  fontSize: 14,
  htmlFontSize: 16,
  fontWeightLight: fontWeights.light,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.medium,
  fontWeightBold: fontWeights.demibold,
  pxToRem: pxToRemConversion
};

const theme = {
  ...defaultTheme,
  palette: pointmentTheme.palette,
  breakpoints: pointmentTheme.breakpoints,
  typography: pointmentTheme.typography
};

export default theme;
